/**
 * ProviderChangeCard
 *
 * Displays a known provider with proposed changes from the AI extraction.
 * Actions: Accept, Edit, Reject — each persists immediately via saveReviewDecision.
 */
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Pencil,
  X,
} from 'lucide-react';
import { useState } from 'react';

import {
  type ReviewDecisionType,
  useSaveReviewDecisionMutation,
} from '@/services/api/treatmentCheckInV2Api';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { EvidenceList } from './EvidenceList';
import type { ProviderUpdateItem, ReviewDecision } from './types';

// ── Helpers ──────────────────────────────────────────────────────────────────

const CHANGE_TYPE_LABELS: Record<string, string> = {
  status_change: 'Status Change',
  new_info: 'New Info',
  frequency_change: 'Frequency Change',
  appointment_update: 'Appointment',
};

const PROVIDER_TREATMENT_STATUS_OPTIONS = [
  'TREATING',
  'COMPLETED',
  'DEVIATING',
  'UNKNOWN',
  'REFERRED_TO',
  'NEVER_TREATED',
] as const;

function coerceTreatmentStatusValue(value: string): string {
  return PROVIDER_TREATMENT_STATUS_OPTIONS.includes(
    value as (typeof PROVIDER_TREATMENT_STATUS_OPTIONS)[number],
  )
    ? value
    : 'UNKNOWN';
}

function changeTypeLabel(changeType: string): string {
  return CHANGE_TYPE_LABELS[changeType] ?? changeType;
}

function confidenceLabel(confidence: number): string {
  if (confidence >= 0.9) return 'High';
  if (confidence >= 0.7) return 'Medium';
  return 'Low';
}

function confidenceVariant(
  confidence: number,
): 'default' | 'secondary' | 'outline' {
  if (confidence >= 0.9) return 'default';
  if (confidence >= 0.7) return 'secondary';
  return 'outline';
}

// ── Props ────────────────────────────────────────────────────────────────────

interface ProviderChangeCardProps {
  item: ProviderUpdateItem;
  matterId: string;
  checkInId: string;
  generationId: string;
  existingDecision?: ReviewDecision | null;
}

// ── Component ────────────────────────────────────────────────────────────────

export const ProviderChangeCard = ({
  item,
  matterId,
  checkInId,
  generationId,
  existingDecision,
}: ProviderChangeCardProps) => {
  const [saveDecision, { isLoading }] = useSaveReviewDecisionMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  const isStale =
    existingDecision !== null &&
    existingDecision !== undefined &&
    existingDecision.generation_id !== generationId;

  const currentDecision = existingDecision?.decision ?? null;

  const handleDecision = (decision: ReviewDecisionType) => {
    let approvedPayload: Record<string, string> | null = null;
    if (decision === 'accepted') {
      approvedPayload = Object.fromEntries(
        Object.entries(item.proposed_changes).map(([key, change]) => [
          key,
          change.new_value,
        ]),
      );
    }

    saveDecision({
      matterId,
      checkInId,
      review_key: item.review_key,
      decision,
      generation_id: generationId,
      approved_payload: approvedPayload,
    });
  };

  const handleEditClick = () => {
    // Pre-fill edit values with the proposed new_values
    const initial = Object.fromEntries(
      Object.entries(item.proposed_changes).map(([key, change]) => [
        key,
        key === 'treatment_status'
          ? coerceTreatmentStatusValue(change.new_value)
          : change.new_value,
      ]),
    );
    setEditValues(initial);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValues({});
  };

  const handleSaveEdit = () => {
    saveDecision({
      matterId,
      checkInId,
      review_key: item.review_key,
      decision: 'edited',
      generation_id: generationId,
      approved_payload: editValues,
    });
    setIsEditing(false);
    setEditValues({});
  };

  return (
    <Card className={isStale ? 'border-amber-500' : undefined}>
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-sm">{item.provider_name}</CardTitle>
          <div className="flex items-center gap-1.5">
            <Badge variant="secondary" className="text-[10px]">
              {changeTypeLabel(item.change_type)}
            </Badge>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant={confidenceVariant(item.match_confidence)}
                  className="text-[10px]"
                >
                  {confidenceLabel(item.match_confidence)}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                Match confidence: {(item.match_confidence * 100).toFixed(0)}% (
                {item.match_type})
              </TooltipContent>
            </Tooltip>
            {isStale && (
              <Badge variant="outline" className="gap-1 text-[10px] text-amber-600">
                <AlertTriangle className="size-3" />
                Stale
              </Badge>
            )}
            {currentDecision && !isStale && (() => {
              const variantMap: Record<string, 'default' | 'destructive' | 'secondary'> = {
                accepted: 'default',
                rejected: 'destructive',
              };
              return (
                <Badge
                  variant={variantMap[currentDecision] ?? 'secondary'}
                  className="text-[10px]"
                >
                  {currentDecision}
                </Badge>
              );
            })()}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        {/* Proposed changes — display or edit mode */}
        <div className="space-y-2">
          {Object.entries(item.proposed_changes).map(([fieldName, change]) => (
            <div key={fieldName} className="space-y-1">
              {isEditing ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="min-w-24 shrink-0 font-medium text-muted-foreground">
                    {change.field}:
                  </span>
                  {fieldName === 'treatment_status' ? (
                    <Select
                      value={coerceTreatmentStatusValue(
                        editValues[fieldName] ?? change.new_value,
                      )}
                      onValueChange={(value) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [fieldName]: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-7 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDER_TREATMENT_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={editValues[fieldName] ?? change.new_value}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [fieldName]: e.target.value,
                        }))
                      }
                      className="h-7 text-sm"
                    />
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-muted-foreground">
                    {change.field}:
                  </span>
                  {change.old_value && (
                    <>
                      <span className="text-muted-foreground line-through">
                        {change.old_value}
                      </span>
                      <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
                    </>
                  )}
                  <span className="font-medium">{change.new_value}</span>
                  <span className="text-[10px] text-muted-foreground">
                    ({change.source})
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Evidence excerpts (collapsible) */}
        <EvidenceList evidence={item.evidence} />

        {/* Notes */}
        {item.notes && (
          <p className="text-xs text-muted-foreground">{item.notes}</p>
        )}
      </CardContent>

      <CardFooter className="gap-2">
        {isEditing ? (
          <>
            <Button
              size="sm"
              disabled={isLoading}
              onClick={handleSaveEdit}
            >
              <Check className="mr-1 size-3" />
              Save Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isLoading}
              onClick={handleCancelEdit}
            >
              <X className="mr-1 size-3" />
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant={currentDecision === 'accepted' && !isStale ? 'default' : 'outline'}
              disabled={isLoading}
              onClick={() => handleDecision('accepted')}
            >
              <Check className="mr-1 size-3" />
              Accept
            </Button>
            <Button
              size="sm"
              variant={currentDecision === 'edited' && !isStale ? 'default' : 'outline'}
              disabled={isLoading}
              onClick={handleEditClick}
            >
              <Pencil className="mr-1 size-3" />
              Edit
            </Button>
            <Button
              size="sm"
              variant={currentDecision === 'rejected' && !isStale ? 'destructive' : 'outline'}
              disabled={isLoading}
              onClick={() => handleDecision('rejected')}
            >
              <X className="mr-1 size-3" />
              Reject
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};
